import React, { useState } from 'react';
import { useI18n } from '../../../../contexts/I18nContext';
import { useCreditConfig } from '../../../../contexts/CreditConfigContext';
import { generateVideo, pollVideoStatus } from '../../../../services/videoGenerationService';
import ErrorModal from '../../../common/ErrorModal';
import { handleUnifiedError } from '../../../../utils/unifiedErrorHandler';
import VideoOptionsSelector from '../VideoOptionsSelector';
import { useVideoOptions } from '../useVideoOptions';

const TextToVideoTab = ({ onResult, onGeneratingChange }) => {
  const { t, language } = useI18n();
  const { getVideoGenerationCost } = useCreditConfig();
  const [prompt, setPrompt] = useState('');
  const {
    model,
    setModel,
    videoMode,
    setVideoMode,
    aspectRatio,
    setAspectRatio,
    videoResolution,
    setVideoResolution,
    videoDuration,
    setVideoDuration,
    modelOptions,
    videoModeOptions,
    aspectRatioOptions,
    videoResolutionOptions,
    videoDurationOptions
  } = useVideoOptions();
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [errorModal, setErrorModal] = useState({ open: false, title: '', message: '' });

  const count = `${prompt.length} / 2000`;
  const settingsTitle = language === 'en' ? 'Video Settings' : language === 'zh-TW' ? '影片設定' : '视频设置';

  const credits = getVideoGenerationCost({
    model,
    videoMode,
    videoResolution,
    videoDuration
  });
  const hasMode = Array.isArray(videoModeOptions) && videoModeOptions.length > 0 ? !!videoMode : true;
  const hasOptions = !!(model && aspectRatio && videoResolution && videoDuration && hasMode);
  const canGenerate = hasOptions && !!prompt.trim();

  const handleGenerate = async () => {
    if (isGenerating) return;
    if (!prompt.trim()) {
      setErrorModal({ open: true, title: t('videoGeneration.veo.common.errorTitle'), message: t('videoGeneration.veo.common.promptRequired') });
      return;
    }
    setIsGenerating(true);
    if (typeof onGeneratingChange === 'function') onGeneratingChange(true);
    setGenerationProgress(0);
    const basis = 90000;
    const start = Date.now();
    const timer = setInterval(() => {
      const elapsed = Date.now() - start;
      const percent = Math.min(98, Math.round((elapsed / basis) * 100));
      setGenerationProgress(percent);
    }, 200);

    try {
      const gen = await generateVideo({
        type: 'TEXT_TO_VIDEO',
        prompt,
        model,
        videoMode,
        aspectRatio,
        videoResolution,
        videoDuration
      });
      let final = null;
      if (gen && gen.id) {
        final = await pollVideoStatus(gen.id, { intervalMs: 2000, timeoutMs: 300000 });
      }
      clearInterval(timer);
      if (final && final.videoUrl) {
        setGenerationProgress(100);
        if (typeof onResult === 'function') {
          onResult({ src: final.videoUrl, payload: final.payload });
        }
      } else {
        throw new Error('NO_RESULT');
      }
      setTimeout(() => {
        setIsGenerating(false);
        setGenerationProgress(0);
        if (typeof onGeneratingChange === 'function') onGeneratingChange(false);
      }, 400);
    } catch (e) {
      clearInterval(timer);
      setIsGenerating(false);
      if (typeof onGeneratingChange === 'function') onGeneratingChange(false);

      if (e?.code === 'FAILED' || e?.code === 'TIMEOUT' || e?.code === 'REQUEST_FAILED') {
        const msg = e?.message || t('videoGeneration.veo.common.requestFailed');
        setErrorModal({ open: true, title: t('videoGeneration.veo.common.errorTitle'), message: msg });
      } else {
        handleUnifiedError(e, t, {
          showToast: false,
          onError: (errorInfo) => {
            setErrorModal({ open: true, title: t('videoGeneration.veo.common.errorTitle'), message: errorInfo.message });
          }
        });
      }
    }
  };

  return (
    <div className="veo2-form">
      <div className="veo2-section">
        <div className="veo2-section__header">
          <div className="veo2-section__title">{t('videoGeneration.veo.common.prompt')}</div>
          <div className="veo2-count">{count}</div>
        </div>
        <div className="veo2-divider" />
        <textarea
          className="veo2-input"
          placeholder={t('videoGeneration.veo.common.promptPlaceholder')}
          maxLength={2000}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
        />
      </div>

      <VideoOptionsSelector
        title={settingsTitle}
        model={model}
        videoMode={videoMode}
        aspectRatio={aspectRatio}
        videoResolution={videoResolution}
        videoDuration={videoDuration}
        modelOptions={modelOptions}
        videoModeOptions={videoModeOptions}
        aspectRatioOptions={aspectRatioOptions}
        videoResolutionOptions={videoResolutionOptions}
        videoDurationOptions={videoDurationOptions}
        onModelChange={setModel}
        onVideoModeChange={setVideoMode}
        onAspectRatioChange={setAspectRatio}
        onVideoResolutionChange={setVideoResolution}
        onVideoDurationChange={setVideoDuration}
      />

      <div className="veo2-actions">
        {typeof credits === 'number' && credits > 0 && (
          <div className="veo2-credits">
            <div className="veo2-credits__bar">
              <span>{t('videoGeneration.veo.common.creditsBar')}</span>
              <span className="veo2-credits__value">{t('videoGeneration.veo.common.creditsValue', { count: credits })}</span>
            </div>
          </div>
        )}
        <button
          className={`veo2-generate ${isGenerating ? 'generating' : canGenerate ? 'is-ready' : 'is-disabled'}`}
          onClick={handleGenerate}
          disabled={isGenerating || !canGenerate}
          style={isGenerating ? { '--progress': generationProgress } : undefined}
        >
          {isGenerating
            ? `${t('aiToolTemplate.generating')} ${Math.round(generationProgress)}% (${typeof credits === 'number' ? credits : 0} ${t('credits.credits')})`
            : `${typeof credits === 'number' ? t('videoGeneration.veo.common.generateButton', { count: credits }) : t('aiToolTemplate.startGeneration')}`}
        </button>
      </div>
      <ErrorModal open={errorModal.open} title={errorModal.title} message={errorModal.message} onClose={()=>setErrorModal({ open: false, title: '', message: '' })} />
    </div>
  );
};

export default TextToVideoTab;
